import React, { useEffect, useMemo, useState } from "react";
import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
import SimpleTable from "../../../Component/Common/SimpleTable";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import RefreshButton from "../../../Component/Common/RefreshButton";
import SearchInput from "../../../Component/Common/SearchInput";
import { BASE_URL } from "../../../config/api";
import { Badge } from "../../../Component/AdminComponents/Badge";
import { formatDateTime } from "../../../Component/Common/formatDateTime";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../Component/Common/Pagination";



export default function CompanyPaymentsList({ project = "warranty" }) {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /* 🔹 FILTERS */
  const [serviceFilter, setServiceFilter] = useState("all");
  const [search, setSearch] = useState("");

  /* ---------------- FETCH PAYMENTS ---------------- */
  const fetchPayments = async () => {
    try {
      setLoading(true);

      const serviceParam =
        serviceFilter !== "all" ? `&service=${serviceFilter}` : "";

      const searchParam =
        search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "";

      const url = `${BASE_URL}admin/payments?project=${project}&page=${page}${serviceParam}${searchParam}`;

      const res = await fetch(url);
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.message || "Failed to fetch payments");
      }

      setPayments(json.data || []);
      setTotalPages(json.last_page || 1);
    } catch (err) {
      console.error("Payments fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  /* reset page when filters/search change */
  useEffect(() => {
    setPage(1);
  }, [serviceFilter, search]);

  useEffect(() => {
    fetchPayments();
  }, [page, project, serviceFilter, search]);

  /* ---------------- BADGE HELPERS ---------------- */
  const getServiceBadgeColor = (service) => {
    switch (service) {
      case "activation":
        return "blue";
      case "claim":
        return "yellow";
      case "update_pay_later":
        return "green";
      default:
        return "gray";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "captured":
        return "green";
      case "failed":
        return "red";
      case "pending":
        return "yellow";
      case "refunded":
        return "gray";
      default:
        return "gray";
    }
  };

  /* ---------------- TABLE ROWS ---------------- */
  const rows = useMemo(() => {
    return payments.map((p) => {
      let meta = {};
      try {
        meta = p.meta ? JSON.parse(p.meta) : {};
      } catch {
        meta = {};
      }

      return {
        id: p.payment_id,  
        raw: p,
        cells: [
          p.payment_id || "-",
          formatDateTime(p.paid_at),
          p.gateway || "-",
          `₹${Number(p.amount).toFixed(2)}`,

          <Badge color={getServiceBadgeColor(p.service)}>
            {p.service || "-"}
          </Badge>,

          <Badge color={getStatusBadgeColor(p.status)}>
            {p.status || "-"}
          </Badge>,

          meta.imei1 || "-",
        ],
      };
    });
  }, [payments]);

  
  const hasData = rows.length > 0;

  /* ---------------- UI ---------------- */
  return (
    <SectionCard
      title="Payments"
      
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end md:gap-4">

        {/* SEARCH */}
        <div className="w-full md:w-64">
          <SearchInput
            value={search}
            onChange={setSearch}
            onClear={() => setSearch("")}
            placeholder="Search Payment ID"
            size="sm"
          />
        </div>

        {/* FILTERS */}
        <div className="flex items-center gap-2 justify-end m-2 text-nowrap">
          <button onClick={() => setServiceFilter("all")}>
            <Badge color={serviceFilter === "all" ? "blue" : "gray"}>
              All
            </Badge>
          </button>

          <button onClick={() => setServiceFilter("activation")}>
            <Badge color={serviceFilter === "activation" ? "blue" : "gray"}>
              Activation
            </Badge>
          </button>

          <button onClick={() => setServiceFilter("update_pay_later")}>
            <Badge color={serviceFilter === "update_pay_later" ? "blue" : "gray"}>
              Update Pay Later
            </Badge>
          </button>

          <button onClick={() => setServiceFilter("claim")}>
            <Badge color={serviceFilter === "claim" ? "blue" : "gray"}>
              Claim
            </Badge>
          </button>
          <div className="hidden lg:block">
            <RefreshButton onClick={fetchPayments} />
          </div>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="py-8 flex justify-center">
          <RegularLoader />
        </div>
      )}

      {/* NO DATA */}
      {!loading && !hasData && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-sm font-semibold text-slate-600">
            No payments found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Payments will appear here once transactions are made.
          </p>
        </div>
      )}

      {/* TABLE VIEW (Desktop) */}
        {!loading && hasData && (
          <div className="hidden md:block">
            <SimpleTable
              headers={[
                "Payment ID",
                "Paid At",
                "Gateway",
                "Amount",
                "Service",
                "Status",
                "IMEI",
              ]}
              rows={rows}
              showSrNo
              renderActions={(row) => (
                <button
                  onClick={() =>
                    navigate(`/company/payment-details/${row.payment_id}`)
                  }
                  className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  View
                </button>
              )}
            />
          </div>
        )}

        {/* MOBILE CARD VIEW */}
        {!loading && hasData && (
          <div className="md:hidden space-y-5">
            {payments.map((p) => {
              let meta = {};
              try {
                meta = p.meta ? JSON.parse(p.meta) : {};
              } catch {}

              const statusColor =
                p.status === "captured"
                  ? "border-green-500"
                  : p.status === "failed"
                  ? "border-red-500"
                  : p.status === "pending"
                  ? "border-yellow-500"
                  : "border-gray-400";

              return (
                <div
                  key={p.payment_id}
                  className={`relative bg-white rounded-2xl border ${statusColor} border-l-4 shadow-sm hover:shadow-md transition-all duration-300 p-5`}
                >
                  {/* Top Row */}
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">
                        Payment ID
                      </p>
                      <p className="text-sm font-semibold text-slate-800 mt-1">
                        {p.payment_id}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-xs text-slate-400">Amount</p>
                      <p className="text-lg font-bold text-slate-900">
                        ₹{Number(p.amount).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Date & Gateway */}
                  <div className="mt-3 text-xs text-slate-500">
                    {formatDateTime(p.paid_at)} • {p.gateway || "N/A"}
                  </div>

                  {/* Divider */}
                  <div className="my-4 border-t border-slate-100" />

                  {/* Badges */}
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <div className="flex gap-2">
                      <Badge color={getServiceBadgeColor(p.service)}>
                        {p.service}
                      </Badge>
                      <Badge color={getStatusBadgeColor(p.status)}>
                        {p.status}
                      </Badge>
                    </div>

                    <button
                      onClick={() =>
                        navigate(`/company/payment-details/${p.payment_id}`)
                      }
                      className="px-4 py-1.5 text-xs font-semibold bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow hover:scale-105 transition"
                    >
                      View
                    </button>
                  </div>

                  {/* IMEI */}
                  <p className="mt-4 text-xs text-slate-600">
                    <span className="font-medium text-slate-700">IMEI:</span>{" "}
                    {meta.imei1 || "-"}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* SHARED PAGINATION */}
            {!loading && hasData && totalPages > 1 && (
              <div className="mt-6 flex justify-center">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
    </SectionCard>
  );
}


// import React, { useEffect, useMemo, useState } from "react";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import SimpleTable from "../../../Component/Common/SimpleTable";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { BASE_URL } from "../../../config/api";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatDateTime } from "../../../Component/Common/formatDateTime";

// export default function CompanyPaymentsList({ project = "warranty" }) {
//   const [payments, setPayments] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   /* 🔹 SERVICE FILTER */
//   const [serviceFilter, setServiceFilter] = useState("all");

//   /* ---------------- FETCH PAYMENTS ---------------- */
//   const fetchPayments = async () => {
//     try {
//       setLoading(true);

//       const serviceParam =
//         serviceFilter !== "all" ? `&service=${serviceFilter}` : "";

//       const url = `${BASE_URL}admin/payments?project=${project}&page=${page}${serviceParam}`;

//       const res = await fetch(url);
//       const json = await res.json();

//       if (!res.ok) {
//         throw new Error(json?.message || "Failed to fetch payments");
//       }

//       setPayments(json.data || []);
//       setTotalPages(json.last_page || 1);
//     } catch (err) {
//       console.error("Payments fetch failed:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* reset page on filter change */
//   useEffect(() => {
//     setPage(1);
//   }, [serviceFilter]);

//   useEffect(() => {
//     fetchPayments();
//   }, [page, project, serviceFilter]);

//   /* ---------------- BADGE HELPERS ---------------- */
//   const getServiceBadgeColor = (service) => {
//     switch (service) {
//       case "activation":
//         return "blue";
//       case "claim":
//         return "yellow";
//       case "renewal":
//         return "green";
//       default:
//         return "gray";
//     }
//   };

//   const getStatusBadgeColor = (status) => {
//     switch (status) {
//       case "captured":
//         return "green";
//       case "failed":
//         return "red";
//       case "pending":
//         return "yellow";
//       case "refunded":
//         return "gray";
//       default:
//         return "gray";
//     }
//   };

//   /* ---------------- TABLE ROWS ---------------- */
//   const rows = useMemo(() => {
//     return payments.map((p) => {
//       let meta = {};
//       try {
//         meta = p.meta ? JSON.parse(p.meta) : {};
//       } catch {
//         meta = {};
//       }

//       return {
//         raw: p,
//         cells: [
//           p.payment_id || "-",
//           formatDateTime(p.paid_at),
//           p.gateway || "-",
//           `₹${Number(p.amount).toFixed(2)}`,

//           <Badge color={getServiceBadgeColor(p.service)}>
//             {p.service || "-"}
//           </Badge>,

//           <Badge color={getStatusBadgeColor(p.status)}>
//             {p.status || "-"}
//           </Badge>,

//           meta.imei1 || "-",
//         ],
//       };
//     });
//   }, [payments]);

//   const hasData = rows.length > 0;

//   /* ---------------- UI ---------------- */
//   return (
//     <SectionCard
//       title="Payments"
//       right={
//         <div className="flex items-center gap-2">
//             <button onClick={() => setServiceFilter("all")}>
//             <Badge color={serviceFilter === "all" ? "blue" : "gray"}>
//                 All
//             </Badge>
//             </button>

//             <button onClick={() => setServiceFilter("activation")}>
//             <Badge color={serviceFilter === "activation" ? "blue" : "gray"}>
//                 Activation
//             </Badge>
//             </button>

//             <button onClick={() => setServiceFilter("claim")}>
//             <Badge color={serviceFilter === "claim" ? "blue" : "gray"}>
//                 Claim
//             </Badge>
//             </button>

//             <RefreshButton onClick={fetchPayments} />
//         </div>
//         }

//     >
//       {/* LOADING */}
//       {loading && (
//         <div className="py-8 flex justify-center">
//           <RegularLoader />
//         </div>
//       )}

//       {/* NO DATA */}
//       {!loading && !hasData && (
//         <div className="flex flex-col items-center justify-center py-10 text-center">
//           <p className="text-sm font-semibold text-slate-600">
//             No payments found
//           </p>
//           <p className="mt-1 text-xs text-slate-500">
//             Payments will appear here once transactions are made.
//           </p>
//         </div>
//       )}

//       {/* TABLE */}
//       {!loading && hasData && (
//         <SimpleTable
//           headers={[
//             "Payment ID",
//             "Paid At",
//             "Gateway",
//             "Amount",
//             "Service",
//             "Status",
//             "IMEI",
//           ]}
//           rows={rows}
//           currentPage={page}
//           totalPages={totalPages}
//           onPageChange={setPage}
//           showSrNo
//         />
//       )}
//     </SectionCard>
//   );
// }



// import React, { useEffect, useMemo, useState } from "react";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import SimpleTable from "../../../Component/Common/SimpleTable";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { BASE_URL } from "../../../config/api";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatDateTime } from "../../../Component/Common/formatDateTime";

// export default function CompanyPaymentsList({ project = "warranty" }) {
//   const [payments, setPayments] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   /* ---------------- FETCH PAYMENTS ---------------- */
//   const fetchPayments = async () => {
//     try {
//       setLoading(true);

//       const url = `${BASE_URL}admin/payments?project=${project}&page=${page}`;
//       const res = await fetch(url);
//       const json = await res.json();

//       if (!res.ok) {
//         throw new Error(json?.message || "Failed to fetch payments");
//       }

//       setPayments(json.data || []);
//       setTotalPages(json.last_page || 1);
//     } catch (err) {
//       console.error("Payments fetch failed:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchPayments();
//   }, [page, project]);


//   const getServiceBadgeColor = (service) => {
//   switch (service) {
//     case "activation":
//       return "blue";
//     case "renewal":
//       return "green";
//     default:
//       return "gray";
//   }
// };

// const getStatusBadgeColor = (status) => {
//   switch (status) {
//     case "captured":
//       return "green";
//     case "failed":
//       return "red";
//     case "pending":
//       return "yellow";
//     case "refunded":
//       return "gray";
//     default:
//       return "gray";
//   }
// };


//   /* ---------------- TABLE ROWS ---------------- */
//   const rows = useMemo(() => {
//   return payments.map((p) => {
//     let meta = {};
//     try {
//       meta = p.meta ? JSON.parse(p.meta) : {};
//     } catch {
//       meta = {};
//     }

//     return {
//       raw: p,
//       cells: [
//         p.payment_id || "-",
//         formatDateTime(p.paid_at || "-"),
//         p.gateway || "-",
//         `₹${Number(p.amount).toFixed(2)}`,
//         /* SERVICE BADGE */
//         <Badge color={getServiceBadgeColor(p.service)}>
//           {p.service || "-"}
//         </Badge>,
//         /* STATUS BADGE */
//         <Badge color={getStatusBadgeColor(p.status)}>
//           {p.status || "-"}
//         </Badge>,

//         meta.imei1 || "-",
        
//       ],
//     };
//   });
// }, [payments]);

//   const hasData = rows.length > 0;

//   /* ---------------- UI ---------------- */
//   return (
//     <SectionCard
//       title="Payments"
//       right={<RefreshButton onClick={fetchPayments} />}
//     >
//       {/* LOADING */}
//       {loading && (
//         <div className="py-8 flex justify-center">
//           <RegularLoader />
//         </div>
//       )}

//       {/* NO DATA */}
//       {!loading && !hasData && (
//         <div className="flex flex-col items-center justify-center py-10 text-center">
//           <p className="text-sm font-semibold text-slate-600">
//             No payments found
//           </p>
//           <p className="mt-1 text-xs text-slate-500">
//             Payments will appear here once transactions are made.
//           </p>
//         </div>
//       )}

//       {/* TABLE */}
//       {!loading && hasData && (
//         <SimpleTable
//           headers={[
//             "Payment ID",
//             "Paid At",
//             "Gateway",
//             "Amount",
//             "Service",
//             "Status",
//             "IMEI",
            
//           ]}
//           rows={rows}
//           currentPage={page}
//           totalPages={totalPages}
//           onPageChange={setPage}
//           showSrNo
//         />
//       )}
//     </SectionCard>
//   );
// }
